

import React, { useState, useEffect } from 'react';
import { Helmet } from "react-helmet";
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import RobotSelection from '../Common/RobotSelection';
import '../../styles/Triggers.css';
import ProcessSelection from '../Common/ProcessSelection';
import LoggingService from "../../services/LoggingService";
import { useTenant } from "../../context/TenantContext";
import FolderSelection from "../Common/FolderSelection";
import config from '../../config/config';
import { toast } from 'react-toastify';



const FilePathTrigger = () => {
  const { selectedTenant } = useTenant();
  const [filePathTriggers, setfilePathTriggers] = useState([]);
  const [name, setName] = useState('');
  const [folder, setFolder] = useState(''); // Add this line
  const [folderPath, setFolderPath] = useState('');
  const [fileFormat, setFileFormat] = useState('');
  const [folderPathError, setFolderPathError] = useState('');
  const [fileFormatError, setFileFormatError] = useState('');
  // First, update the process state
  const [process, setProcess] = useState({ id: "", dbId: "", name: "" });
  const [argumentsList, setArgumentsList] = useState([]);
  // Remove this unused state
  const [processes, setProcesses] = useState([]);
  const [selectedTriggerId, setSelectedTriggerId] = useState(null);
  const [inputArguments, setInputArguments] = useState(false);
  const [robot, setRobot] = useState('All');
  const [priority, setPriority] = useState('Inherited');
  const [stickToRobot, setStickToRobot] = useState(false);  // Add this line

  const priorities = [
    { value: "Inherited", label: "Inherited", icon: "○" },
    { value: "Critical", label: "Critical", icon: "◊" },
    { value: "Highest", label: "Highest", icon: "≫" },
    { value: "Very high", label: "Very high", icon: "≫" },
    { value: "High", label: "High", icon: "∧" },
    { value: "Medium-high", label: "Medium-high", icon: "⋀" },
    { value: "Medium", label: "Medium", icon: "=" },
    { value: "Medium-low", label: "Medium-low", icon: "⋁" },
    { value: "Low", label: "Low", icon: "∨" },
    { value: "Very low", label: "Very low", icon: "≪" },
    { value: "Lowest", label: "Lowest", icon: "≪" }
  ];

  const validateFolderPath = (path) => {
    if (!path) {
      setFolderPathError('Folder path is required');
      return false;
    }

    // Allow all characters except Windows invalid ones
    const invalidChars = /[<>"|?*]/;
    if (invalidChars.test(path)) {
      setFolderPathError('Path contains invalid characters (< > " | ? *)');
      return false;
    }

    // Basic path format validation
    if (!path.match(/^([a-zA-Z]:\\|\\\\|\/)/)) {
      setFolderPathError('Invalid path format. Use either:\n- Local path (C:\\Folder\\SubFolder)\n- Network path (\\\\Server\\Share\\Folder)\n- Unix path (/home/user/folder)');
      return false;
    }

    // Check path length
    let maxLength;
    if (path.startsWith('\\\\')) maxLength = 32767;
    else if (path.startsWith('/')) maxLength = 4096;
    else maxLength = 260;

    if (path.length > maxLength) {
      setFolderPathError(`Path is too long. Maximum length is ${maxLength} characters`);
      return false;
    }

    setFolderPathError('');
    return true;
  };

  const validateFileFormat = (format) => {
    if (!format) {
      setFileFormatError('File format is required');
      return false;
    }

    // Check for basic format (either .ext or *.ext)
    const validFormatRegex = /^(\*\.[a-zA-Z0-9]+|\.[a-zA-Z0-9]+)$/;
    if (!validFormatRegex.test(format)) {
      setFileFormatError('Please enter a valid file format (e.g., .xlsx or *.pdf)');
      return false;
    }

    // Check format length
    if (format.length > 10) {
      setFileFormatError('File format is too long');
      return false;
    }

    // Check for common file formats (optional validation)
    const commonFormats = ['.txt', '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv', '.json', '.xml'];
    if (!format.startsWith('*.') && !commonFormats.includes(format)) {
      setFileFormatError('Warning: Uncommon file format. Please verify if correct.');
      return false;
    }

    setFileFormatError('');
    return true;
  };

  const navigate = useNavigate();
  const fetchFilePathTriggers = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const response = await axios.get(`${config.API_BASE_URL}/api/filePathTrigger`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setfilePathTriggers(response.data);
    } catch (error) {
      console.error('Error fetching file path triggers:', error);
    }
  };
  useEffect(() => {
    // Placeholder for fetching process data from the backend

    setArgumentsList([
      { name: 'arg1', value: '' },
      { name: 'arg2', value: '' }
    ]);
  }, []);


  const handleArgumentChange = (index, value) => {
    const updatedArguments = argumentsList?.map((arg, i) =>
      i === index ? { ...arg, value } : arg
    );
    setArgumentsList(updatedArguments);
  };

  const handleArgumentNameChange = (index, value) => {
    const updatedArguments = argumentsList?.map((arg, i) =>
      i === index ? { ...arg, name: value } : arg
    );
    setArgumentsList(updatedArguments);
  };

  const handleFilePathTrigger = async () => {
    // Validate inputs before proceeding
    const isValidPath = validateFolderPath(folderPath);
    const isValidFormat = validateFileFormat(fileFormat);

    if (!isValidPath || !isValidFormat) {
      return;
    }

    try {
      const token = localStorage.getItem('userToken');
      const userId = localStorage.getItem('userId');

      if (!userId) {
        console.error("User ID not found in localStorage");
        return;
      }

      const payload = {
        name,
        status: 'Active',
        createdBy: userId,
        filePathConfig: {
          folderPath,
          fileFormat,
          process: process.id,
          priority: priority,
          stickto_robot: stickToRobot  // Add this line
        },
        robot_id: robot,
        argumentsList: argumentsList.filter(arg => arg.name && arg.value),
        folderId: folder
      };

      console.log('Sending payload:', payload); // Debug log

      const response = await axios.post(`${config.API_BASE_URL}/api/filePathTrigger`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      LoggingService.log(
        `User ${name} created a File Path Trigger: Name="${name}", Folder="${folderPath}", File Format="${fileFormat}", Process="${process.id}", Robot="${robot}", Arguments="${JSON.stringify(argumentsList)}"`
      );

      toast.success("File Path Trigger created successfully!");
      navigate('/dashboard');
    } catch (error) {
      console.error("Error creating file path trigger:", error.response?.data || error);
      toast.error(error.response?.data?.error || "Failed to create trigger. Please try again.");
    }
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('userToken');
      await axios.delete(`${config.API_BASE_URL}/api/filePathTrigger/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchFilePathTriggers(); // Refresh the list after deletion
    } catch (error) {
      console.error('Error deleting email trigger:', error);
    }
  };

  const handleEdit = (trigger) => {
    setSelectedTriggerId(trigger.id);
    setProcess(trigger.processToExecute || '');
    setInputArguments(trigger.hasInputArguments || false);
  };

  return (
    <div className="trigger-container">
      <Helmet>
        <title>Create File Path Trigger</title>
      </Helmet>
      <h2>Create File Path Trigger</h2>

      {!selectedTenant ? (
        <p className="error-message">Please select a tenant first.</p>
      ) : (
        <>
          <div className="form-group">
            <label>Trigger Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter Trigger Name"
            />
          </div>

          <div className="form-group">
            <label>Folder Path *</label>
            <input
              type="text"
              value={folderPath}
              onChange={(e) => {
                setFolderPath(e.target.value);
                validateFolderPath(e.target.value);
              }}
              placeholder="e.g., C:\Users\Documents or /home/user/documents"
              className={folderPathError ? 'error-input' : ''}
              title="Enter a valid path:
          • Windows local drive (e.g., C:\\Folder)
          • Network path (e.g., \\\\Server\\Share)
          • Unix path (e.g., /home/user/folder)
          • No special characters
          • Windows path max: 260 chars
          • Network path max: 32767 chars
          • Unix path max: 4096 chars"
            />
            {folderPathError && <div className="error-message">{folderPathError}</div>}
          </div>

          <div className="form-group">
            <label>File Format *</label>
            <input
              type="text"
              value={fileFormat}
              onChange={(e) => {
                setFileFormat(e.target.value);
                validateFileFormat(e.target.value);
              }}
              placeholder=".xlsx, .pdf, .txt"
              className={fileFormatError ? 'error-input' : ''}
            />
            <div className="input-tooltip">
              Enter the file extension to monitor (e.g., .xlsx or *.pdf). Include the dot.
            </div>
            {fileFormatError && <div className="error-message">{fileFormatError}</div>}
          </div>


          <div className="form-group">
            <div style={{ display: 'flex', gap: 8, justifyContent: "space-between", alignItems: "center" }}>
              <label>Input Arguments</label>
              <button style={{ marginTop: 0 }} onClick={() => {
                let rry = [...argumentsList]
                rry.push({ name: `arg${argumentsList.length + 1}`, value: '' });
                setArgumentsList(rry)
              }}>+</button>
            </div>
            {argumentsList?.length > 0 ? (
              argumentsList?.map((arg, index) => (
                <div key={index} className="argument" style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                  <input
                    type="text"
                    value={arg.name}
                    onInput={(e) => handleArgumentNameChange(index, e.target.value)}
                    placeholder="Name"
                  />
                  <input
                    type="text"
                    value={arg.value}
                    onInput={(e) => handleArgumentChange(index, e.target.value)}
                    placeholder="Value"
                  />
                  <button style={{ marginTop: 0 }} className='delete-button-small' onClick={() => {
                    let rry = [...argumentsList]
                    rry = rry.filter((f, i) => {
                      return i !== index
                    })
                    setArgumentsList(rry)
                  }}>Remove</button>
                </div>
              ))
            ) : (
              <p>No input arguments defined.</p>
            )}
          </div>

          <div className="form-group">
            <FolderSelection folder={folder} onChange={setFolder} isGlobal={true} />
          </div>

          <div className="form-group">
            <RobotSelection robot={robot} onChange={setRobot} folder={folder} />
          </div>

          <div className="form-group">
            <ProcessSelection
              process={process}
              onChange={(selected) => {
                console.log('Selected process:', selected);
                if (selected) {
                  setProcess({
                    id: selected.id,    // Just use id and name
                    name: selected.name
                  });
                } else {
                  setProcess({ id: "", name: "" });
                }
              }}
              folder={folder}
            />
          </div>
          <div className="form-group process-robot-lock">
            <div className="robot-lock-container">
              <input
                type="checkbox"
                id="stickToRobot"
                checked={stickToRobot}
                onChange={(e) => setStickToRobot(e.target.checked)}
                disabled={robot === 'All'}
              />
              <label htmlFor="stickToRobot" className="robot-lock-label">
                <span className="lock-icon">🔒</span>
                Lock process to selected robot
              </label>
            </div>
            {robot === 'All' ? (
              <small className="help-text warning">
                Select a specific robot to enable process locking
              </small>
            ) : (
              <small className="help-text">
                This process will only be executed by the selected robot
              </small>
            )}
          </div>



          <div className="form-group">
            <label>Priority</label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className="form-control"
            >
              {priorities.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.icon} {p.label}
                </option>
              ))}
            </select>
          </div>


          <button

            type="button"
            onClick={handleFilePathTrigger}
            className="submit-button"
          >
            {selectedTriggerId ? 'Update Trigger' : 'Save Trigger'}
          </button>

        </>
      )}

    </div>
  );
};

export default FilePathTrigger;

